// src/pages/school/Exam/StudentResultsPage.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Container, Row, Col, Card, Button, Form, Badge, InputGroup, Spinner,
  Table, Alert, Modal,
} from "react-bootstrap";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import {
  collection, doc, getDocs, getDoc, onSnapshot, query, where,
  limit as qLimit
} from "firebase/firestore";
import { ref as sref, getBlob } from "firebase/storage";
import { db, storage } from "../../../services/firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FixedSizeList as List, areEqual } from "react-window";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import * as XLSX from "xlsx";

/* ---------------- helpers ---------------- */
const TOAST_ID = "student-results-page";
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, h:mm A") => (d ? dayjs(d).format(f) : "—");
const lower = (s) => String(s || "").trim().toLowerCase();
dayjs.extend(duration);

const uidForRow = (r) =>
  r?.uid || r?.id || r?.byStudent?.uid || r?.byStudent?.id || "";

const extractIndexUrl = (err) => {
  try {
    const m = String(err?.message || "").match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i);
    return m ? m[0] : "";
  } catch { return ""; }
};

const getTestType = (ex) =>
  String(ex?.testType ?? ex?.examTestType ?? ex?.type ?? ex?.category ?? "").trim();

const computeTimeTaken = (row) => {
  if (Number.isFinite(row?.timeTakenSec) && row.timeTakenSec > 0) {
    const t = row.timeTakenSec;
    const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = Math.floor(t % 60);
    return `${h}h ${m}m ${s}s`;
  }
  const s = toDate(row?.startedAt), e = toDate(row?.submittedAt);
  if (s && e) {
    const diff = Math.max(0, Math.floor((e.getTime() - s.getTime()) / 1000));
    const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), ss = Math.floor(diff % 60);
    return `${h}h ${m}m ${ss}s`;
  }
  return "—";
};

/* --- mobile detection --- */
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const on = () => setIs(window.innerWidth <= bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return is;
}

/* --- avatar goodies --- */
const initialsOf = (name = "") =>
  String(name).trim().split(/\s+/, 2).map(p => p[0]?.toUpperCase() || "").join("") || "ST";
const hueOf = (str = "student") => {
  let h = 0; for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360; return h;
};
const avatarStyle = (name = "") => {
  const h = hueOf(name);
  return {
    background: `linear-gradient(135deg, hsl(${h},70%,55%), hsl(${(h + 40) % 360},70%,55%))`,
    color: "#fff"
  };
};

/* --- scoring helpers --- */
const readObtained = (a = {}) => {
  const v = a.evaluatedScore ?? a.score ?? a.marksObtained ?? a.obtained ?? a.points ?? null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const readTotalMarks = (a = {}) => {
  const v = a.totalMarks ?? a.maxMarks ?? a.fullMarks ?? a.total ?? a.outOf ?? null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const obtainedFromResults = (a = {}) => {
  if (!a || typeof a.resultsByQuestion !== "object") return null;
  try {
    return Object.values(a.resultsByQuestion).reduce((s, r) => s + (Number(r?.marks) || 0), 0);
  } catch { return null; }
};

/* --- student field mapping helpers --- */
const pickFirst = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  return "";
};
const extractFromAttempt = (a = {}) => ({
  name:  pickFirst(a.studentName, a.name, a.byStudent?.name, a.byStudent?.fullName),
  roll:  pickFirst(a.roll, a.byStudent?.rollNo, a.byStudent?.roll, a.admissionNo, a.regNo),
  className: pickFirst(a.className, a.byStudent?.className, a.byStudent?.class, a.class, a.grade, a.std),
  section:   pickFirst(a.section, a.byStudent?.section, a.byStudent?.sec),
  branch:    pickFirst(a.branch, a.byStudent?.branch, a.byStudent?.stream, a.byStudent?.department, a.stream, a.department, a.group, a.program),
});
const extractFromProfile = (p = {}) => ({
  name:  pickFirst(p.name, p.fullName, p.displayName, p.studentName, p.profile?.name),
  roll:  pickFirst(p.roll, p.rollNo, p.rollno, p.roll_number, p.admissionNo, p.regNo, p.enrollmentNo),
  className: pickFirst(p.className, p.class, p.grade, p.std, p.standard),
  section:   pickFirst(p.section, p.sec),
  branch:    pickFirst(p.branch, p.stream, p.department, p.group, p.program)
});

/* --- secure PDF viewer --- */
function usePdfViewer() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);
  const [title, setTitle] = useState("Answer PDF");

  const openAttemptPdf = useCallback(async (attempt) => {
    if (!attempt?.upload) { toast.warn("No uploaded file found.", { containerId: TOAST_ID }); return; }
    const up = attempt.upload;
    setBusy(true);
    setTitle(up?.name || "Answer PDF");
    try {
      if (up.path) {
        const r = sref(storage, up.path);
        const blob = await getBlob(r);
        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
        setShow(true);
      } else if (up.url) {
        window.open(up.url, "_blank", "noopener");
      } else {
        toast.warn("Upload metadata is missing.", { containerId: TOAST_ID });
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't open PDF (no access or missing file).", { containerId: TOAST_ID });
    } finally { setBusy(false); }
  }, []);

  const close = () => setShow(false);

  const PdfModal = useMemo(() => function PdfModalComp() {
    if (!show) return null;
    return (
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}
        onClick={close}
      >
        <div className="shadow-lg rounded bg-white" style={{ width: "92vw", height: "85vh" }} onClick={(e) => e.stopPropagation()}>
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <div className="fw-semibold">{title}</div>
            <div className="d-flex gap-2">
              {objectUrl && <a href={objectUrl} download={title || "answer.pdf"} className="btn btn-sm btn-primary">Download</a>}
              <Button size="sm" variant="secondary" onClick={close}>Close</Button>
            </div>
          </div>
          <div style={{ height: "calc(100% - 46px)" }}>
            {objectUrl ? (
              <iframe title="Answer PDF" src={objectUrl} style={{ border: 0, width: "100%", height: "100%" }} />
            ) : (
              <div className="d-flex h-100 w-100 align-items-center justify-content-center"><Spinner /></div>
            )}
          </div>
        </div>
      </div>
    );
  }, [show, objectUrl, title]);

  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  return { openAttemptPdf, PdfModal, busy };
}

/* ---------------- Row (desktop, virtualized) ---------------- */
const RowItem = React.memo(function RowItem({ index, style, data }) {
  const row = data.rows[index];
  const openPdf = data.openPdf;
  const onViewExam = data.onViewExam;

  const obtained = Number.isFinite(row.obtained) ? row.obtained : readObtained(row);
  const total    = Number.isFinite(row.total)    ? row.total    : readTotalMarks(row);
  const pct = Number.isFinite(row.percent) ? Math.round(row.percent) : (total > 0 ? Math.round((100 * obtained) / total) : null);
  const timeTaken = computeTimeTaken(row);

  const displayName = row.studentName || row.name || row.byStudent?.name || row.byStudent?.fullName || "—";
  const classSec = [row.className || "", row.section || ""].filter(Boolean).join("-");
  const branch = row.branch || "";

  return (
    <div style={style} className={`px-2`}>
      <div className="d-flex align-items-center py-2 row-anim" style={{ minWidth: 1440  }}>
        {/* Student */}
        <div style={{ width: 320 }} className="pe-2 d-flex align-items-center gap-2">
          <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
               style={{ width: 36, height: 36, fontWeight: 700, fontSize: 12, ...avatarStyle(displayName) }}>
            {initialsOf(displayName)}
          </div>
          <div className="min-w-0">
            <div className="fw-semibold text-truncate">{displayName}</div>
            <div className="small text-muted d-flex flex-wrap gap-1 align-items-center">
              {(row.roll || row.byStudent?.rollNo) && <span>Roll: {row.roll || row.byStudent?.rollNo}</span>}
              {classSec && <span className="chip">{classSec}</span>}
              {branch && <span className="chip chip-info">{branch}</span>}
            </div>
          </div>
        </div>

        {/* Score & meta */}
        <div style={{ width: 110 }} className="pe-2">{Number.isFinite(obtained) ? obtained : "—"}/{total || "—"}</div>
        <div style={{ width: 70 }} className="pe-2">{pct ?? "—"}%</div>
        <div style={{ width: 90 }} className="pe-2">{row.accuracy != null ? `${Math.round(row.accuracy)}%` : "—"}</div>
        <div style={{ width: 110 }} className="pe-2">{timeTaken}</div>
        <div style={{ width: 70 }} className="pe-2">{row.rank ?? "—"}</div>
        <div style={{ width: 90 }} className="pe-2">{row.percentile != null ? Math.round(row.percentile) : "—"}</div>
        <div style={{ width: 170 }} className="pe-2">{fmt(toDate(row.submittedAt))}</div>
 {/* Subject-wise */}
        <div style={{ width: 260 }} className="pe-2">
          <div className="d-flex flex-wrap gap-1">
            {row.subjectStats
              ? Object.entries(row.subjectStats).map(([s, v]) => (
                  <Badge key={s} bg="light" text="dark" title={`${s}: ${Math.round((v.obtained ?? 0)*100)/100}/${v.total} • Rank ${v.rank ?? "—"}`}>
                    {s}: {Math.round((v.percent ?? 0))}% (R{v.rank ?? "—"})
                  </Badge>
                ))
              : <span className="text-muted">—</span>}
          </div>
        </div>
        {/* Status + actions */}
        <div style={{ width: 120 }} className="pe-2">
          <Badge bg={
            row.status === "submitted" ? "success" :
            row.status === "in-progress" ? "info" :
            row.status === "invalidated" ? "danger" : "secondary"
          }>
            {String(row.status || "—").toUpperCase()}
          </Badge>
        </div>
        <div style={{ width: 210 }} className="d-flex gap-2">
          {row.upload && (
            <Button
              size="sm" variant="outline-secondary"
              onClick={() => openPdf(row)}
              title={row.upload?.path ? "Open securely (Storage rules)" : "Open link"}
            >
              View PDF
            </Button>
          )}
          <Button size="sm" variant="outline-primary" onClick={() => onViewExam(row)} title="View exam details">
            View Exam
          </Button>
        </div>
      </div>
    </div>
  );
}, areEqual);

/* ---------------- Row (mobile, virtualized card) ---------------- */
const MobileRowItem = React.memo(function MobileRowItem({ index, style, data }) {
  const r = data.rows[index];
  const openPdf = data.openPdf;
  const onViewExam = data.onViewExam;

  const obtained = Number.isFinite(r.obtained) ? r.obtained : readObtained(r);
  const total    = Number.isFinite(r.total)    ? r.total    : readTotalMarks(r);
  const pct = Number.isFinite(r.percent) ? Math.round(r.percent) : (total > 0 ? Math.round((100 * obtained) / total) : null);

  const name = r.studentName || r.name || r.byStudent?.name || r.byStudent?.fullName || "—";

  return (
    <div style={{ ...style, padding: "6px 6px 0 6px" }}>
      <Card className="border-0 shadow-sm mb-2" style={{ height: 86 }}>
        <Card.Body className="py-2">
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34, fontWeight: 700, fontSize: 11, ...avatarStyle(name) }}>
              {initialsOf(name)}
            </div>
            <div className="flex-grow-1 min-w-0">
              <div className="fw-semibold text-truncate">{name}</div>
              <div className="small text-muted d-flex flex-wrap gap-1">
                {(r.roll || r.byStudent?.rollNo) && <span className="chip">Roll {r.roll || r.byStudent?.rollNo}</span>}
                {[r.className, r.section].filter(Boolean).join("-") && (
                  <span className="chip">{[r.className, r.section].filter(Boolean).join("-")}</span>
                )}
                {r.branch && <span className="chip chip-info">{r.branch}</span>}
              </div>
               <div className="d-flex gap-1 flex-wrap ms-2">
              {r.subjectStats
                ? Object.entries(r.subjectStats).map(([s, v]) => (
                    <Badge key={s} bg="light" text="dark">
                      {s}: {Math.round((v.percent ?? 0))}% (R{v.rank ?? "—"})
                    </Badge>
                  ))
                : null}
            </div>
              
            </div>
            
            <Badge bg={
              r.status === "submitted" ? "success" :
              r.status === "in-progress" ? "info" :
              r.status === "invalidated" ? "danger" : "secondary"
            }>{String(r.status || "—").toUpperCase()}</Badge>
          </div>

          <div className="d-flex align-items-center justify-content-between mt-2">
            <div className="small">
              <b>{Number.isFinite(obtained) ? obtained : "—"}/{total || "—"}</b>&nbsp;•&nbsp;
              <b>{pct ?? "—"}%</b>&nbsp;•&nbsp;
              Rank {r.rank ?? "—"}
            </div>
            <div className="d-flex gap-1">
              {r.upload && (
                <Button size="sm" variant="outline-secondary" onClick={() => openPdf(r)}>PDF</Button>
              )}
              <Button size="sm" onClick={() => onViewExam(r)}>Details</Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}, areEqual);

/** -----------------------------------------------------------------------
 * EmbeddedQuestionAnalysis
 * ------------------------------------------------------------------------ */
function EmbeddedQuestionAnalysis({ schoolId, examId, uid }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);

  // filters
  const [fSubject, setFSubject] = useState("all");
  const [fTopic, setFTopic] = useState("all");
  const [fType, setFType] = useState("all");
  const [fCorrect, setFCorrect] = useState("all");
  const [q, setQ] = useState("");

  /* ===== helpers (scoped) ===== */
  const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
  const norm = (s) => String(s || "").trim().toLowerCase();

  
  const readTotal = (obj = {}) => {
    const v = obj.totalMarks ?? obj.maxScore ?? obj.fullMarks ?? obj.total ?? obj.outOf ?? 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };


  const getCaseCorrectKey = (sq = {}) => {
    const viaKey = String(sq.correctKey || "").trim().toUpperCase();
    if (viaKey) return viaKey;
    const opt = (sq.options || []).find((o) => o?.isCorrect);
    return opt?.key ? String(opt.key).toUpperCase() : "";
  };
  const subIdFor = (parentQ, sq, idx) => sq?.id || `${parentQ.id}__sub_${idx}`;
  const getAnsValue = (ans) => (ans && typeof ans === "object" && "value" in ans ? ans.value : ans);
 
  const buildARStemHtml = (q) => {
    const head = q.questionHtml ? `<div>${q.questionHtml}</div>` : "";
    const a = q.assertionHtml || "";
   
    const r = q.reasonHtml || "";
    return `
      ${head}
      <div><strong>Assertion (A):</strong> ${a || "<em>—</em>"}</div>
      <div><strong>Reason (R):</strong> ${r || "<em>—</em>"}</div>
    `;
  };
  const getMarks = (q) => Number(q.marks ?? q.maxMarks ?? 1) || 1;



// keep this ABOVE the data-loading useEffect
const evaluateAttemptLocally = React.useCallback((attempt, questions) => {
  // --- tiny helpers kept INSIDE, so there are no external deps ---
  const norm = (s) => String(s || "").trim().toLowerCase();
  const getMarks = (q) => Number(q.marks ?? q.maxMarks ?? 1) || 1;
  const getNegMarks = (q) => Math.max(0, Number(q.negativeMarks ?? q.negative ?? q.neg ?? 0) || 0);
  const subIdFor = (parentQ, sq, idx) => sq?.id || `${parentQ.id}__sub_${idx}`;
  const getAnsValue = (ans) => (ans && typeof ans === "object" && "value" in ans ? ans.value : ans);
  const toLetterKey = (v) => {
    const n = Number(v);
    if (Number.isFinite(n) && String(v) !== "") return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
    return String(v ?? "").trim().toUpperCase();
  };
  const getCaseCorrectKey = (sq = {}) => {
    const viaKey = String(sq.correctKey || "").trim().toUpperCase();
    if (viaKey) return viaKey;
    const opt = (sq.options || []).find((o) => o?.isCorrect);
    return opt?.key ? String(opt.key).toUpperCase() : "";
  };
  const readCaseChoice = (answers = {}, parentQ, subIndex, sq) => {
    const bySubId = answers[subIdFor(parentQ, sq, subIndex)];
    if (bySubId != null) return getAnsValue(bySubId);
    const parent = answers[parentQ?.id];
    if (parent && typeof parent === "object" && parent.value && typeof parent.value === "object") {
      return parent.value[subIndex];
    }
    return null;
  };
  const getCorrectShape = (q = {}) => {
    const qt = norm(q.questionType);
    if (qt === "mcq" || qt === "mcq_descriptive") {
      const opt = (q.options || []).find((o) => o?.isCorrect);
      return { type: "key", value: opt?.key ?? null };
    }
    if (qt === "multi_correct") {
      const keys = (q.options || []).filter((o) => o?.isCorrect).map((o) => String(o.key));
      return { type: "keyset", value: keys };
    }
    if (qt === "true_false" || qt === "tf") return { type: "scalar", value: String(q.trueFalseAnswer || "True") };
    if (qt === "match") {
      if (Number.isInteger(q.correctOptionIndex)) return { type: "index", value: Number(q.correctOptionIndex) };
      return { type: "unknown", value: null };
    }
    if (qt === "assertion_reason" || qt === "ar") return { type: "scalar", value: String(q.arAnswer || "A") };
    return { type: "unknown", value: null };
  };
  const isAnswerCorrect = (q, studentValue, correctShape) => {
    const qt = norm(q.questionType);
    const { type, value } = correctShape;
    if (type === "unknown") return null;

    const toKey = (v) => String(v ?? "").trim().toUpperCase();
    const toKeyArray = (v) =>
      Array.isArray(v)
        ? v.map((x) => toKey(x))
        : String(v ?? "")
            .split(",")
            .map((x) => toKey(x))
            .filter(Boolean);

    if (qt === "mcq" || qt === "mcq_descriptive") {
      if (type !== "key") return null;
      const sv = studentValue;
      const sKey =
        Number.isFinite(Number(sv)) && String(sv) !== ""
          ? String.fromCharCode(65 + Number(sv))
          : toKey(sv);
      return sKey && value ? sKey === toKey(value) : null;
    }
    if (qt === "multi_correct") {
      if (type !== "keyset") return null;
      const sKeys = new Set(toKeyArray(studentValue));
      const cKeys = new Set(value.map((x) => toKey(x)));
      if (sKeys.size !== cKeys.size) return false;
      for (const k of sKeys) if (!cKeys.has(k)) return false;
      return true;
    }
    if (qt === "true_false" || qt === "tf") {
      if (type !== "scalar") return null;
      return String(studentValue) === String(value);
    }
    if (qt === "match") {
      if (type !== "index") return null;
      return Number(studentValue) === Number(value);
    }
    if (qt === "assertion_reason" || qt === "ar") {
      const toLetter = (v) => {
        const n = Number(v);
        if (Number.isFinite(n)) return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
        return String(v || "").toUpperCase();
      };
      const sKey = toLetter(studentValue);
      const cKey = toLetter(value);
      return sKey && cKey ? sKey === cKey : null;
    }
    return null;
  };

  // ---- evaluation loop ----
  if (!attempt || !questions?.length) {
    const score = Number(attempt?.score ?? attempt?.evaluatedScore ?? attempt?.marksObtained ?? attempt?.obtained ?? attempt?.points ?? 0) || 0;
    const total = Number(attempt?.totalMarks ?? attempt?.maxMarks ?? attempt?.fullMarks ?? attempt?.total ?? attempt?.outOf ?? 0) || 0;
    return {
      resultsByQuestion: {},
      answeredCount: Number(attempt?.answeredCount ?? 0),
      correctCount: Number(attempt?.correctCount ?? 0),
      score,
      total
    };
  }

  const answers = attempt.answers || {};
  const resultsByQuestion = {};
  let total = 0, score = 0, answered = 0, correct = 0;

  for (const q of questions) {
    const qt = norm(q.questionType);
    if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
      for (const [idx, sq] of q.caseSubQs.entries()) {
        const subId = subIdFor(q, sq, idx);
        const subMarks = Number(sq.marks ?? q.marks ?? 1) || 1;
        const subNeg = Number(sq.negativeMarks ?? q.negativeMarks ?? 0) || 0;
        total += subMarks;

        const raw = readCaseChoice(answers, q, idx, sq);
        const chosenKey = toLetterKey(getAnsValue(raw));
        const isAnswered = chosenKey !== "";
        if (isAnswered) answered += 1;

        const correctKey = getCaseCorrectKey(sq);
        const ok = chosenKey && correctKey ? chosenKey === correctKey : null;

        if (ok === true) {
          score += subMarks; correct += 1;
          resultsByQuestion[subId] = { correct: true, marks: subMarks, evaluated: true, parentId: q.id };
        } else if (ok === false) {
          score -= subNeg;
          resultsByQuestion[subId] = { correct: false, marks: -subNeg, evaluated: true, parentId: q.id };
        } else {
          resultsByQuestion[subId] = { correct: false, marks: 0, evaluated: false, parentId: q.id };
        }
      }
    } else {
      const marks = getMarks(q);
      const neg = getNegMarks(q);
      total += marks;

      const raw = answers[q.id];
      const ansVal = getAnsValue(raw);
      const isAnswered = Array.isArray(ansVal) ? ansVal.length > 0 : String(ansVal ?? "").trim() !== "";
      if (isAnswered) answered += 1;

      const correctShape = getCorrectShape(q);
      const ok = isAnswerCorrect(q, ansVal, correctShape);

      if (ok === null) {
        resultsByQuestion[q.id] = { correct: false, marks: 0, evaluated: false };
      } else if (ok) {
        score += marks; correct += 1;
        resultsByQuestion[q.id] = { correct: true, marks, evaluated: true };
      } else {
        score -= neg;
        resultsByQuestion[q.id] = { correct: false, marks: -neg, evaluated: true };
      }
    }
  }

  return { resultsByQuestion, answeredCount: answered, correctCount: correct, score, total };
}, []); // empty deps -> stable reference

  /* ===== load data ===== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true); setErr("");
        if (!schoolId || !examId || !uid) {
          setErr("Missing context.");
          setLoading(false);
          return;
        }
        const exRef = doc(db, "schools", schoolId, "exams", examId);
        const exSnap = await getDoc(exRef);
        if (!exSnap.exists()) throw new Error("Exam not found.");
        const ex = { id: exSnap.id, ...exSnap.data() };
        if (!mounted) return;
        setExam(ex);

        let qs = [];
        if (ex.templateId) {
          const qCol = collection(db, "schools", schoolId, "questions");
          const qy = query(qCol, where("templateId", "==", ex.templateId), qLimit(800));
          const qSnap = await getDocs(qy);
          qs = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }
        if (!mounted) return;
        setQuestions(qs);

        const attRef = doc(db, "schools", schoolId, "exams", examId, "attempts", uid);
        const attSnap = await getDoc(attRef);
        const rawAttempt = attSnap.exists() ? { id: attSnap.id, ...attSnap.data() } : null;

        if (rawAttempt) {
          const evald = evaluateAttemptLocally(rawAttempt, qs);
          const merged = {
            ...rawAttempt,
            resultsByQuestion: evald.resultsByQuestion,
            answeredCount: evald.answeredCount,
            correctCount: evald.correctCount,
            score: evald.score,
            totalMarks: evald.total,
            evaluatedScore: evald.score,
          };
          if (!mounted) return;
          setAttempt(merged);
        } else {
          if (!mounted) return;
          setAttempt(null);
        }
      } catch (e) {
        if (!mounted) return;
        console.error(e);
        setErr(e?.message || "Failed to load analysis.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [schoolId, examId, uid,evaluateAttemptLocally]);

  /* ===== derived ===== */
  const scoreInfo = useMemo(() => {
    if (!attempt) return { score: 0, total: 0, percent: 0, answered: 0, correct: 0, accuracy: 0, timeSec: 0 };
    const score = Number.isFinite(attempt.score) ? Number(attempt.score) : Number(attempt.evaluatedScore || 0);
    let total = Number(attempt.totalMarks || readTotal(attempt));
    if (!total && questions.length) {
      total = questions.reduce((sum, q) => {
        const qt = String(q.questionType || "").toLowerCase();
        if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
          return sum + q.caseSubQs.reduce((s, sq) => s + (Number(sq.marks ?? q.marks ?? 1) || 1), 0);
        }
        return sum + getMarks(q);
      }, 0);
    }
    const answered = Number(attempt.answeredCount ?? 0);
    const correct = Number(attempt.correctCount ?? 0);
    const percent = total ? Math.max(0, Math.min(100, (score / total) * 100)) : 0;
    const accuracy = answered ? Math.max(0, Math.min(100, (correct / answered) * 100)) : 0;

    const start = toJS(attempt.startedAt);
    const end = toJS(attempt.submittedAt);
    const timeSec = start && end ? Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000)) : 0;

    return { score, total, percent, answered, correct, accuracy, timeSec };
  }, [attempt, questions]);

  const timeLabel = useMemo(() => {
    const s = scoreInfo.timeSec || 0;
    const d = dayjs.duration(s, "seconds");
    const hh = String(Math.floor(d.asHours())).padStart(2, "0");
    const mm = String(d.minutes()).padStart(2, "0");
    const ss = String(d.seconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }, [scoreInfo.timeSec]);

  const qItems = useMemo(() => {
    if (!attempt || !questions.length) return [];
    const rbq = attempt.resultsByQuestion && typeof attempt.resultsByQuestion === "object" ? attempt.resultsByQuestion : {};
    const answers = attempt.answers || {};
    const items = [];
    const typeLabel = (qt) => {
      const t = norm(qt);
      if (t === "mcq") return "MCQ";
      if (t === "multi_correct") return "Multi Correct";
      if (t === "true_false") return "True/False";
      if (t === "match") return "Match";
      if (t === "assertion_reason" || t === "ar") return "A/R";
      if (t === "case_based") return "Case";
      if (t === "mcq_descriptive") return "MCQ+Desc";
      if (t === "descriptive") return "Descriptive";
      if (t === "statement_based") return "Statement";
      return (qt || "").toUpperCase();
    };
     const readCaseChoice = (answers = {}, parentQ, subIndex, sq) => {
    const bySubId = answers[subIdFor(parentQ, sq, subIndex)];
    if (bySubId != null) return getAnsValue(bySubId);
    const parent = answers[parentQ?.id];
    if (parent && typeof parent === "object" && parent.value && typeof parent.value === "object") {
      return parent.value[subIndex];
    }
    return null;
  };
    const toKey = (v) => (Number.isFinite(Number(v)) && String(v) !== "" ? String.fromCharCode(65 + Number(v)) : String(v ?? "").toUpperCase());
    const parentExpMap = new Map(questions.map((q) => [q.id, q.explanationHtml || ""]));

    questions.forEach((q) => {
      const qt = norm(q.questionType);

      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        q.caseSubQs.forEach((sq, idx) => {
          const qid = subIdFor(q, sq, idx);
          const evalInfo = rbq[qid] || {};
          const studentRaw = readCaseChoice(answers, q, idx, sq);
          const studentVal = getAnsValue(studentRaw);
          const chosenKey = toKey(studentVal);
          const correctKey = getCaseCorrectKey(sq);
          const awarded = Number(evalInfo?.marks ?? 0) || 0;
          const marks = Number(sq.marks ?? q.marks ?? 1) || 1;
          const correctness = evalInfo.evaluated ? (evalInfo.correct ? "correct" : "wrong") : chosenKey ? "wrong" : "unattempted";

          items.push({
            id: qid,
            parentId: q.id,
            indexLabel: `Case ${idx + 1}`,
            subject: q.subject || "General",
            topic: q.topic || "—",
            typeLabel: "Case",
            questionHtml: sq.questionHtml || "",
            passageHtml: q.casePassageHtml || q.passageHtml || "",
            explanationHtml: parentExpMap.get(q.id) || "",
            options: sq.options || [],
            chosen: chosenKey || "",
            correct: correctKey || "",
            awarded,
            marks,
            correctness,
            sectionKey: q.sectionKey || "",
          });
        });
        return;
      }

      const qid = q.id;
      const evalInfo = rbq[qid] || {};
      const studentRaw = answers[qid];
      const studentVal = getAnsValue(studentRaw);
      const answered = String(studentVal ?? "").trim() !== "";
      const correctness = evalInfo.evaluated ? (evalInfo.correct ? "correct" : "wrong") : answered ? "wrong" : "unattempted";

      let chosenLabel = "", correctLabel = "";
      const tLabel = typeLabel(q.questionType);
      const stemHtml = qt === "assertion_reason" || qt === "ar" ? buildARStemHtml(q) : (q.questionHtml || "");

      if (qt === "mcq" || qt === "mcq_descriptive") {
        const chosen = toKey(studentVal);
        const correct = (q.options || []).find((o) => o.isCorrect)?.key ?? "";
        chosenLabel = chosen; correctLabel = correct;
      } else if (qt === "multi_correct") {
        const chosen = Array.isArray(studentVal)
          ? studentVal
          : String(studentVal || "").split(",").map((s) => s.trim()).filter(Boolean);
        const correct = (q.options || []).filter((o) => o.isCorrect).map((o) => o.key);
        chosenLabel = chosen.map((x) => String(x).toUpperCase()).join(", ");
        correctLabel = correct.join(", ");
      } else if (qt === "true_false") {
        chosenLabel = String(studentVal || ""); correctLabel = String(q.trueFalseAnswer || "True");
      } else if (qt === "match") {
        chosenLabel = Number.isInteger(studentVal) && studentVal >= 0 && studentVal <= 3 ? `Option ${Number(studentVal) + 1}` : "";
        correctLabel = Number.isInteger(q.correctOptionIndex) && q.correctOptionIndex >= 0 ? `Option ${q.correctOptionIndex + 1}` : "";
      } else if (qt === "assertion_reason" || qt === "ar") {
        const toLetter = (v) => {
          const n = Number(v);
          if (Number.isFinite(n)) return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
          return String(v || "").toUpperCase();
        };
        chosenLabel = toLetter(studentVal); correctLabel = toLetter(q.arAnswer || "A");
      } else {
        chosenLabel = String(studentVal ?? ""); correctLabel = "—";
      }

      items.push({
        id: qid,
        parentId: null,
        indexLabel: "",
        subject: q.subject || "General",
        topic: q.topic || "—",
        typeLabel: tLabel,
        questionHtml: stemHtml,
        passageHtml: q.passageHtml || "",
        explanationHtml: q.explanationHtml || "",
        options: q.options || [],
        chosen: chosenLabel,
        correct: correctLabel,
        awarded: Number(evalInfo?.marks ?? 0) || 0,
        marks: getMarks(q),
        correctness,
        sectionKey: q.sectionKey || "",
      });
    });

    return items;
  }, [attempt, questions]);

  /* ===== filter options ===== */
  const subjectOptions = useMemo(
    () => ["all", ...Array.from(new Set(qItems.map((i) => i.subject))).sort()],
    [qItems]
  );
  const topicOptions = useMemo(() => {
    const list = Array.from(new Set(qItems.map((i) => i.topic).filter(Boolean)));
    return ["all", ...list.sort()];
  }, [qItems]);
  const typeOptions = useMemo(() => {
    const list = Array.from(new Set(qItems.map((i) => i.typeLabel)));
    return ["all", ...list.sort()];
  }, [qItems]);

  /* ===== filtered list ===== */
  const filteredQItems = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return qItems.filter((i) => {
      if (fSubject !== "all" && i.subject !== fSubject) return false;
      if (fTopic !== "all" && i.topic !== fTopic) return false;
      if (fType !== "all" && i.typeLabel !== fType) return false;
      if (fCorrect !== "all" && i.correctness !== fCorrect) return false;

      if (needle) {
        const plainQ = (i.questionHtml || "").replace(/<[^>]*>/g, " ").toLowerCase();
        const plainPass = (i.passageHtml || "").replace(/<[^>]*>/g, " ").toLowerCase();
        const hay = [
          i.subject,
          i.topic,
          i.typeLabel,
          i.correct,
          i.chosen,
          plainQ,
          plainPass,
        ]
          .map((s) => String(s || "").toLowerCase())
          .join(" ");
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [qItems, fSubject, fTopic, fType, fCorrect, q]);

  /* ===== expand/collapse state ===== */
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ===== render ===== */
  if (loading) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" size="sm" /> Loading analysis…
      </div>
    );
  }
  if (err) {
    return <Alert variant="danger" className="mb-0">{err}</Alert>;
  }
  if (!attempt || !exam) {
    return <Alert variant="warning" className="mb-0">No analysis available.</Alert>;
  }
  const wrongCount = Math.max(0, (attempt.answeredCount ?? 0) - (attempt.correctCount ?? 0));
const answeredCount   = attempt.answeredCount ?? 0;
const totalQuestions  = questions.length;
const unattemptedCount = Math.max(0, totalQuestions - answeredCount);
  return (
    <div>
      {/* Summary header (compact for modal) */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div className="min-w-0">
              <div className="fw-bold text-truncate">{exam.name || "Exam"}</div>
              <div className="small text-muted">
                Score:&nbsp;<b>{Math.round(scoreInfo.score * 100) / 100}</b> / {scoreInfo.total || "—"} &nbsp;•&nbsp;
                Percent:&nbsp;<b>{Math.round(scoreInfo.percent)}%</b> &nbsp;•&nbsp;
                Accuracy:&nbsp;<b>{Math.round(scoreInfo.accuracy)}%</b> &nbsp;•&nbsp;
                Time:&nbsp;<b>{timeLabel}</b>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Badge bg={scoreInfo.percent >= 75 ? "success" : scoreInfo.percent >= 50 ? "info" : scoreInfo.percent >= 33 ? "warning" : "danger"}>
                {Math.round(scoreInfo.percent)}%
              </Badge>
                <Badge bg="info">{unattemptedCount} Unattempted</Badge>
              <Badge bg="primary">{attempt.answeredCount ?? 0} Attempted</Badge>
              <Badge bg="success">{attempt.correctCount ?? 0} Correct</Badge>
                <Badge bg="danger">{wrongCount} Wrong</Badge>
              
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text className="bg-light">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search question / subject / topic / your answer"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                {subjectOptions.map((s) => (
                  <option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fTopic} onChange={(e) => setFTopic(e.target.value)}>
                {topicOptions.map((t) => (
                  <option key={t} value={t}>{t === "all" ? "All topics" : t}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fType} onChange={(e) => setFType(e.target.value)}>
                {typeOptions.map((t) => (
                  <option key={t} value={t}>{t === "all" ? "All types" : t}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fCorrect} onChange={(e) => setFCorrect(e.target.value)}>
                <option value="all">All</option>
                <option value="correct">Correct</option>
                <option value="wrong">Wrong</option>
                <option value="unattempted">Unattempted</option>
              </Form.Select>
            </Col>
          </Row>

          <div className="mt-2 d-flex gap-2 flex-wrap align-items-center">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setQ("");
                setFSubject("all");
                setFTopic("all");
                setFType("all");
                setFCorrect("all");
              }}
            >
              Reset
            </Button>
            <div className="small text-muted">
              Showing <b>{filteredQItems.length}</b> of {qItems.length}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Question-wise table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
          <div>Question-wise Analysis</div>
          <div className="small text-muted">Tap a row to expand details</div>
        </Card.Header>
        <Card.Body className="p-0">
          {filteredQItems.length === 0 ? (
            <div className="p-3 text-muted">Nothing to show. Try changing filters.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 52 }} className="text-center">#</th>
                  <th>Subject / Topic</th>
                  <th className="d-none d-sm-table-cell">Type</th>
                  <th className="text-center d-none d-md-table-cell">Your Answer</th>
                  <th className="text-center d-none d-md-table-cell">Correct</th>
                  <th className="text-center">Marks</th>
                  <th className="text-center">Result</th>
                </tr>
              </thead>
              <tbody>
                {filteredQItems.map((it, idx) => {
                  const isOpen = expanded.has(it.id);
                  return (
                    <React.Fragment key={it.id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleRow(it.id)}
                        className={isOpen ? "table-active" : undefined}
                      >
                        <td className="text-center">{it.indexLabel || idx + 1}</td>
                        <td>
                          <div className="fw-semibold">{it.subject}</div>
                          <div className="text-muted small text-truncate" title={`${it.topic}${it.sectionKey ? ` • Sec ${it.sectionKey}` : ""}`}>
                            {it.topic} {it.sectionKey ? `• Sec ${it.sectionKey}` : ""}
                          </div>
                          {/* mobile quick answers */}
                          <div className="d-md-none mt-1 small">
                            <span className="me-2">You: {it.chosen || "—"}</span>
                            <span className="text-muted">Correct: {it.correct || "—"}</span>
                          </div>
                        </td>
                        <td className="d-none d-sm-table-cell">
                          <Badge bg="light" text="dark">{it.typeLabel}</Badge>
                        </td>
                        <td className="text-center d-none d-md-table-cell">
                          {it.chosen ? <Badge bg="light" text="dark">{it.chosen}</Badge> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center d-none d-md-table-cell">
                          {it.correct ? <Badge bg="info" text="dark">{it.correct}</Badge> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          <span className={it.awarded > 0 ? "text-success" : it.awarded < 0 ? "text-danger" : "text-muted"}>
                            {it.awarded > 0 ? `+${it.awarded}` : it.awarded}
                          </span>{" "}
                          <span className="text-muted">/ {it.marks}</span>
                        </td>
                        <td className="text-center">
                          {it.correctness === "correct" && <Badge bg="success">Correct</Badge>}
                          {it.correctness === "wrong" && <Badge bg="danger">Wrong</Badge>}
                          {it.correctness === "unattempted" && <Badge bg="secondary">Unattempted</Badge>}
                        </td>
                      </tr>

                      {/* detail row */}
                      {isOpen && (
                        <tr>
                          <td colSpan={7} className="p-3" style={{ background: "rgba(0,0,0,.02)" }}>
                            {/* Passage (if any) */}
                            {it.passageHtml ? (
                              <div className="mb-3">
                                <div className="text-muted small">Passage</div>
                                <div dangerouslySetInnerHTML={{ __html: it.passageHtml }} />
                              </div>
                            ) : null}

                            {/* Question stem */}
                            <div className="mb-2">
                              <div className="text-muted small">Question</div>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: it.questionHtml || "<em class='text-muted'>No content</em>",
                                }}
                              />
                            </div>

                            {/* A/R helper legend */}
                            {it.typeLabel === "A/R" && (
                              <div className="mt-2 small text-muted">
                                <ol className="mb-0">
                                  <li>A — Both true, Reason explains Assertion</li>
                                  <li>B — Both true, Reason does not explain Assertion</li>
                                  <li>C — Assertion true, Reason false</li>
                                  <li>D — Assertion false</li>
                                </ol>
                              </div>
                            )}

                            {/* Options */}
                            {(it.options || []).length > 0 && (
                              <Row className="g-2 mb-2 mt-2">
                                {it.options.map((o) => {
                                  const chosenSet = new Set(
                                    (it.chosen || "")
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                  );
                                  const correctSet = new Set(
                                    (it.correct || "")
                                      .split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean)
                                  );
                                  const isChosen = chosenSet.has(o.key);
                                  const isCorrect = correctSet.has(o.key);
                                  const variant = isCorrect ? "success" : isChosen ? "danger" : "light";
                                  return (
                                    <Col md={6} key={o.key}>
                                      <Card className="h-100" border={isCorrect ? "success" : isChosen ? "danger" : "light"}>
                                        <Card.Body className="py-2">
                                          <div className="d-flex align-items-start gap-2">
                                            <div style={{ width: 28 }}>
                                              <Badge bg={variant} text={variant === "light" ? "dark" : undefined}>
                                                {o.key}
                                              </Badge>
                                            </div>
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                              <div
                                                dangerouslySetInnerHTML={{
                                                  __html: o.value || "<em class='text-muted'>—</em>",
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </Card.Body>
                                      </Card>
                                    </Col>
                                  );
                                })}
                              </Row>
                            )}

                            {/* Explanation */}
                            {it.explanationHtml ? (
                              <>
                                <hr />
                                <div className="text-muted small">Explanation</div>
                                <div
                                  className="mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html: it.explanationHtml || "<em class='text-muted'>No explanation provided.</em>",
                                  }}
                                />
                              </>
                            ) : (
                              <div className="text-muted small">
                                <em>No explanation provided.</em>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <style>{`
        /* subtle hover + mobile niceties inside modal */
        .table-hover tbody tr:hover { transition: background-color .2s ease; }
        @media (max-width: 576px) {
          .table thead th:nth-child(3),
          .table thead th:nth-child(4),
          .table thead th:nth-child(5) { display: none !important; }
        }
      `}</style>
    </div>
  );
}

/* ===================== Page ===================== */
export default function StudentResultsPage() {
  const isMobile = useIsMobile(768);
  const [schoolId] = useState(() => localStorage.getItem("schoolId"));

  // filters
  const [testType, setTestType] = useState("");
  const [examId, setExamId] = useState("");
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [, setIndexUrl] = useState("");
  const [examTotal, setExamTotal] = useState(0);

  const [attempts, setAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [branch, setBranch] = useState("");
  const [status, ] = useState("");
  const [q, setQ] = useState("");
  const [scoreMin, ] = useState(0);
  const [scoreMax, ] = useState(100);
  const [hasPdf, setHasPdf] = useState(false);

  const { openAttemptPdf, PdfModal } = usePdfViewer();

  // details modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const openDetail = useCallback((row) => { setDetailRow(row); setDetailOpen(true); }, []);
  const closeDetail = () => setDetailOpen(false);

  // list height controls
  const ITEM_SIZE_DESKTOP = 64;
  const ITEM_SIZE_MOBILE  = 92;
  const MAX_LIST_HEIGHT   = 520;
  const MIN_LIST_HEIGHT   = 140; // keeps header+1 row comfy
   // subject-wise (computed from questions + resultsByQuestion)
  const [questions, setQuestions] = useState([]);
  const [subjectTotals, setSubjectTotals] = useState({}); // { [subject]: totalMarks }

  // sub-id builder (match EmbeddedQuestionAnalysis)
  const subIdFor = (parentQ, sq, idx) => sq?.id || `${parentQ.id}__sub_${idx}`;

  /* --- compute exam max marks + load questions + subject totals --- */
  useEffect(() => {
    if (!schoolId || !examId) { setExamTotal(0); setQuestions([]); setSubjectTotals({}); return; }
    (async () => {
      try {
        const exRef = doc(db, "schools", schoolId, "exams", examId);
        const exSnap = await getDoc(exRef);
        const ex = exSnap.exists() ? { id: exSnap.id, ...exSnap.data() } : null;

        let total = 0;
        let qs = [];

        if (ex?.maxMarks) total = Number(ex.maxMarks) || 0;

        if (ex?.templateId) {
          const qCol = collection(db, "schools", schoolId, "questions");
          const qy = query(qCol, where("templateId", "==", ex.templateId), qLimit(1200));
          const qSnap = await getDocs(qy);
          qs = qSnap.docs.map(d => ({ id: d.id, ...d.data() }));

          if (!total) {
            total = qs.reduce((sum, q) => {
              const qt = String(q.questionType || "").toLowerCase();
              if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
                return sum + q.caseSubQs.reduce((s, sq) =>
                  s + (Number(sq.marks ?? q.marks ?? 1) || 1), 0);
              }
              return sum + (Number(q.marks ?? 1) || 1);
            }, 0);
          }
        }

        // build subjectTotals from questions
        const subjTotals = {};
        qs.forEach(q => {
          const subject = q.subject || "General";
          const qt = String(q.questionType || "").toLowerCase();
          if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
            q.caseSubQs.forEach(sq => {
              const m = Number(sq.marks ?? q.marks ?? 1) || 1;
              subjTotals[subject] = (subjTotals[subject] || 0) + m;
            });
          } else {
            const m = Number(q.marks ?? 1) || 1;
            subjTotals[subject] = (subjTotals[subject] || 0) + m;
          }
        });

        setQuestions(qs);
        setSubjectTotals(subjTotals);
        setExamTotal(total || 0);
      } catch {
        setQuestions([]);
        setSubjectTotals({});
        setExamTotal(0);
      }
    })();
  }, [schoolId, examId]);


/* --- load exams --- */
useEffect(() => {
  if (!schoolId) return;

  setLoadingExams(true);
  const ref = collection(db, "schools", schoolId, "exams");

  const unsub = onSnapshot(
    query(ref, qLimit(500)),
    (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort(
        (a, b) =>
          (toDate(b.createdAt) || toDate(b.date) || 0) -
          (toDate(a.createdAt) || toDate(a.date) || 0)
      );

      setExams(list);
      setLoadingExams(false);

      // 🔑 Preserve “All Exams” (empty string) if the user picked it
      if (examId === "") return;

      // Otherwise, if current examId vanished, fallback to first exam
      if (!list.some(x => x.id === examId)) {
        setExamId(list[0]?.id || "");
      }
    },
    () => setLoadingExams(false)
  );

  return () => unsub();
}, [schoolId, examId]);
const testTypeOptions = useMemo(() => {
    const set = new Set();
    exams.forEach(ex => { const t = getTestType(ex); if (t) set.add(t); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [exams]);

  const filteredExams = useMemo(() => {
    if (!testType) return exams;
    return exams.filter(ex => getTestType(ex) === testType);
  }, [exams, testType]);

useEffect(() => {
  // ✅ Preserve "All Exams" selection
  if (examId === "") return;

  // If a specific exam was selected but vanished from the filtered list, fallback
  if (!filteredExams.some(e => e.id === examId)) {
    setExamId(filteredExams[0]?.id || "");
  }
}, [filteredExams, examId]);


  /* --- fetch student profiles (students + fallback users) --- */
  const fetchStudentProfiles = useCallback(
    async (arg) => {
      const uniq = (arr = []) => Array.from(new Set(arr.filter(Boolean)));
      let byStudentIds = [], byUids = [];

      if (Array.isArray(arg)) {
        const ids = uniq(arg); byStudentIds = ids; byUids = ids;
      } else {
        byStudentIds = uniq(arg?.byStudentIds);
        byUids = uniq(arg?.byUids);
      }

      const byId = {};
      const byUid = {};
      const users = {};

      const studentsCol = collection(db, "schools", schoolId, "students");
      const usersCol = collection(db, "users");
      const chunks = (arr, n = 10) => {
        const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out;
      };

      // by student doc id
      for (const chunk of chunks(byStudentIds)) {
        const snap = await getDocs(query(studentsCol, where("__name__", "in", chunk)));
        snap.docs.forEach((d) => {
          const v = { id: d.id, ...d.data() };
          byId[d.id] = v;
          if (v?.auth?.uid) byUid[v.auth.uid] = v;
        });
      }

      // by auth uid (rows where attempt only has uid)
      const uidsStillNeeded = byUids.filter((u) => !byUid[u]);
      for (const chunk of chunks(uidsStillNeeded)) {
        const snap = await getDocs(query(studentsCol, where("auth.uid", "in", chunk)));
        snap.docs.forEach((d) => {
          const v = { id: d.id, ...d.data() };
          byId[d.id] = v;
          if (v?.auth?.uid) byUid[v.auth.uid] = v;
        });
      }

      // fallback /users for names/emails
      for (const chunk of chunks(byUids)) {
        const snap = await getDocs(query(usersCol, where("__name__", "in", chunk)));
        snap.docs.forEach((d) => { users[d.id] = { id: d.id, ...d.data() }; });
      }

      return { byId, byUid, users };
    },
    [schoolId]
  );

 // 🔁 Load attempts for ONE exam or for ALL filtered exams when examId === ""
/* --- load attempts (single exam OR all exams) --- */
useEffect(() => {
  if (!schoolId) { setAttempts([]); return; }

  setLoadingAttempts(true);
  setIndexUrl("");

  // Which exams should we read attempts from?
  const targetExamIds =
    examId === ""
      ? filteredExams.map(e => e.id) // All Exams currently visible in the exam filter
      : [examId].filter(Boolean);

  if (targetExamIds.length === 0) {
    setAttempts([]);
    setLoadingAttempts(false);
    return;
  }

  // Keep one listener per exam; merge rows as they arrive
  const perExamRows = new Map();
  const unsubs = [];

  const attachListener = (exId) => {
    const base = collection(db, "schools", schoolId, "exams", exId, "attempts");
    const wheres = [];
    if (cls)   wheres.push(where("className", "==", cls));
    if (sec)   wheres.push(where("section", "==", sec));
    if (status)wheres.push(where("status", "==", status));

    const qref = query(base, ...wheres, qLimit(2000));

    const unsub = onSnapshot(
      qref,
      async (snap) => {
        // Tag each row with its exam id (helps when merging)
        const rows = snap.docs.map(d => ({ id: d.id, _examId: exId, ...d.data() }));
        rows.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));

        // Update this exam’s rows in our map and merge all
        perExamRows.set(exId, rows);
        const mergedAll = Array.from(perExamRows.values()).flat();

        // Gather profile ids/uids across ALL currently merged rows
        const needStudentIds = Array.from(new Set(mergedAll.map(r => r.byStudent?.id).filter(Boolean)));
        const needUids       = Array.from(new Set(mergedAll.map(r => r.uid || r.id).filter(Boolean)));

        let prof = { byId: {}, byUid: {}, users: {} };
        try {
          prof = await fetchStudentProfiles({ byStudentIds: needStudentIds, byUids: needUids });
        } catch { /* ignore profile failures, we’ll still render rows */ }

        // Quick lookup map for exam name -> id
        const examNameById = new Map(exams.map(e => [e.id, e.name || e.id]));

        // Enrich each row with student meta and keep exam name
        const enriched = mergedAll.map(r => {
          const sid = r.byStudent?.id || null;
          const uid = r.uid || r.id || r.byStudent?.uid || null;
          const p = (sid && prof.byId[sid]) || (uid && prof.byUid[uid]) || null;

          const fromA = extractFromAttempt(r);
          const fromP = extractFromProfile(p || {});
          return {
            ...r,
            examName: examNameById.get(r._examId) || r._examId,
            studentName: pickFirst(r.studentName, r.name, fromA.name, fromP.name, prof.users[uid]?.displayName),
            roll:        pickFirst(r.roll, fromA.roll, fromP.roll),
            className:   pickFirst(r.className, fromA.className, fromP.className),
            section:     pickFirst(r.section, fromA.section, fromP.section),
            branch:      pickFirst(r.branch, fromA.branch, fromP.branch),
            byStudent: {
              id: sid || p?.id,
              name: pickFirst(r.byStudent?.name, fromA.name, fromP.name),
              rollNo: pickFirst(r.byStudent?.rollNo, fromA.roll, fromP.roll),
              uid: uid || p?.auth?.uid || r.byStudent?.uid || undefined,
              ...r.byStudent
            }
          };
        });

        setAttempts(enriched);
        setLoadingAttempts(false);
      },
      (e) => {
        const maybe = extractIndexUrl(e);
        if (maybe) setIndexUrl(maybe);
        setLoadingAttempts(false);
      }
    );

    unsubs.push(unsub);
  };

  // Attach listeners for every target exam
  targetExamIds.forEach(attachListener);

  // Cleanup every listener on unmount/change
  return () => {
    unsubs.forEach(u => u && u());
  };
// IMPORTANT deps:
// - include `examId` and `filteredExams` so "All Exams" state & exam filter changes are respected
}, [schoolId, examId, filteredExams, cls, sec, status, fetchStudentProfiles, exams]);



  /* --- compute obtained/total/% and rank & percentile + subject-wise --- */
  const attemptsEnriched = useMemo(() => {
    if (!attempts.length) return [];

    // Build quick lookup: question id -> subject, and also map sub-ids
    const qIdToSubject = new Map();
    const qIdToMarks   = new Map();
    questions.forEach(q => {
      const subject = q.subject || "General";
      const qt = String(q.questionType || "").toLowerCase();
      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        q.caseSubQs.forEach((sq, idx) => {
          const sid = subIdFor(q, sq, idx);
          qIdToSubject.set(sid, subject);
          qIdToMarks.set(sid, Number(sq.marks ?? q.marks ?? 1) || 1);
        });
      } else {
        qIdToSubject.set(q.id, subject);
        qIdToMarks.set(q.id, Number(q.marks ?? 1) || 1);
      }
    });

    // 1) per-attempt aggregates
    const rows = attempts.map(a => {
      const hasObtField = ["evaluatedScore","score","marksObtained","obtained","points"].some(k => a[k] != null);
      const obtained =
        (hasObtField ? readObtained(a) : null) ??
        obtainedFromResults(a) ?? 0;

      const total =
        readTotalMarks(a) ??
        (a.analysis?.totalMarks != null ? Number(a.analysis.totalMarks) : null) ??
        examTotal ?? 0;

      const percent = total > 0 ? (obtained / total) * 100 : null;

      // subject-wise obtained using resultsByQuestion (if present)
      const sbj = {};
      if (a && a.resultsByQuestion && typeof a.resultsByQuestion === "object") {
        Object.entries(a.resultsByQuestion).forEach(([qid, res]) => {
          const subject = qIdToSubject.get(qid);
          if (!subject) return; // question not found in template
          const marks = Number(res?.marks ?? 0) || 0; // includes negative marking if any
          sbj[subject] = (sbj[subject] || 0) + marks;
        });
      }

      // attach subject totals / percents now
      const perSubject = {};
      Object.keys(subjectTotals).forEach(s => {
        const tot = subjectTotals[s] || 0;
        const ob  = sbj[s] ?? 0;
        const pct = tot > 0 ? (ob / tot) * 100 : null;
        perSubject[s] = { obtained: ob, total: tot, percent: pct, rank: null };
      });

      return { ...a, obtained, total, percent, subjectStats: perSubject };
    });

    // 2) overall ranks
    const sortedOverall = [...rows].sort((x, y) => {
      const px = Number.isFinite(x.percent) ? x.percent : -1;
      const py = Number.isFinite(y.percent) ? y.percent : -1;
      if (py !== px) return py - px;
      const tx = toDate(x.submittedAt)?.getTime() ?? Infinity;
      const ty = toDate(y.submittedAt)?.getTime() ?? Infinity;
      return tx - ty;
    });
    const idToRank = new Map(sortedOverall.map((r, i) => [r.id, i + 1]));
    const N = sortedOverall.length;
    const pctList = sortedOverall.map(r => (Number.isFinite(r.percent) ? r.percent : -1));

    // 3) percentile + inject overall rank
    rows.forEach(r => {
      r.rank = idToRank.get(r.id) ?? null;
      let below = 0;
      if (Number.isFinite(r.percent)) for (const v of pctList) if (v < r.percent) below++;
      r.percentile = N ? (below / N) * 100 : null;
    });

    // 4) subject-wise ranks
    Object.keys(subjectTotals).forEach(subject => {
      const arr = rows
        .map(r => ({
          id: r.id,
          pct: r.subjectStats?.[subject]?.percent ?? null,
          submittedAt: toDate(r.submittedAt)?.getTime() ?? Infinity
        }))
        .sort((a, b) => {
          const pa = Number.isFinite(a.pct) ? a.pct : -1;
          const pb = Number.isFinite(b.pct) ? b.pct : -1;
          if (pb !== pa) return pb - pa;
          return a.submittedAt - b.submittedAt;
        });
      const rankMap = new Map(arr.map((x, i) => [x.id, i + 1]));
      rows.forEach(r => {
        if (r.subjectStats?.[subject]) {
          r.subjectStats[subject].rank = rankMap.get(r.id) ?? null;
        }
      });
    });

    return rows;
  }, [attempts, examTotal, questions, subjectTotals]);


  /* --- dropdown options --- */
  const classOptions = useMemo(() => {
    const set = new Set();
    attemptsEnriched.forEach(a => { if (a.className) set.add(String(a.className)); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [attemptsEnriched]);

  const sectionOptions = useMemo(() => {
    const set = new Set();
    attemptsEnriched.forEach(a => { if (a.section) set.add(String(a.section)); });
    return Array.from(set).sort();
  }, [attemptsEnriched]);

  const branchOptions = useMemo(() => {
    const set = new Set();
    attemptsEnriched.forEach(a => { if (a.branch) set.add(String(a.branch)); });
    return Array.from(set).sort();
  }, [attemptsEnriched]);

  /* --- filtering --- */
  const filtered = useMemo(() => {
    const text = lower(q);
    return attemptsEnriched.filter(a => {
      if (text) {
        const nm = lower(a.studentName || a.name || a.byStudent?.name || "");
        const rl = lower(a.roll || a.byStudent?.rollNo || "");
        if (!nm.includes(text) && !rl.includes(text)) return false;
      }
      if (branch && String(a.branch || "") !== branch) return false;
      const p = Number.isFinite(a.percent) ? a.percent : null;
      if (p != null && (p < scoreMin || p > scoreMax)) return false;

      if (hasPdf) {
        const up = a.upload;
        if (!up || (!up.path && !up.url)) return false;
      }
      return true;
    });
  }, [attemptsEnriched, q, scoreMin, scoreMax, hasPdf, branch]);

  // compute dynamic list height
  const listHeight = useMemo(() => {
    const size = isMobile ? ITEM_SIZE_MOBILE : ITEM_SIZE_DESKTOP;
    const h = size * filtered.length;
    return Math.min(MAX_LIST_HEIGHT, Math.max(MIN_LIST_HEIGHT, h || MIN_LIST_HEIGHT));
  }, [isMobile, filtered.length]);

  /* --- KPIs / Histogram --- */
  const kpi = useMemo(() => {
    const n = filtered.length;
    const submitted = filtered.filter(r => r.status === "submitted" || r.status === "evaluated").length;
    const vals = filtered.map(r => (Number.isFinite(r.percent) ? r.percent : null)).filter(v => Number.isFinite(v));
    const avgPct = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
    let topper = null, bestPct = -1;
    filtered.forEach(r => { const p = Number.isFinite(r.percent) ? r.percent : null; if (p != null && p > bestPct) { bestPct = p; topper = r; } });
    return { count: n, submitted, avgPct, topper: topper ? { name: topper.studentName || topper.name || topper.byStudent?.name || "—", pct: Math.round(bestPct) } : null };
  }, [filtered]);

  const hist = useMemo(() => {
    const bins = Array.from({ length: 10 }).map((_, i) => ({ label: `${i * 10}-${i * 10 + 9}`, count: 0 }));
    filtered.forEach(r => {
      const p = Number.isFinite(r.percent) ? r.percent : null;
      if (p == null) return;
      const idx = Math.max(0, Math.min(9, Math.floor(p / 10)));
      bins[idx].count += 1;
    });
    return bins;
  }, [filtered]);

  /* --- exports --- */
  const exportXLSX = () => {
    const subjKeys = Object.keys(subjectTotals);
    const rows = filtered.map(r => {
      const base = {
        Name: r.studentName || r.name || r.byStudent?.name || "",
        Roll: r.roll || r.byStudent?.rollNo || "",
        Class: r.className || "",
        Section: r.section || "",
        Branch: r.branch || "",
        Obtained: Number.isFinite(r.obtained) ? r.obtained : "",
        Total: r.total ?? "",
        Percent: Number.isFinite(r.percent) ? Math.round(r.percent) : "",
        Accuracy: Number.isFinite(r.accuracy) ? Math.round(r.accuracy) + "%" : "",
        TimeTakenSec: r.timeTakenSec ?? "",
        Rank: r.rank ?? "",
        Percentile: Number.isFinite(r.percentile) ? Math.round(r.percentile) : "",
        Status: r.status || "",
        SubmittedAt: fmt(toDate(r.submittedAt)),
        HasPDF: r.upload && (r.upload.path || r.upload.url) ? "Yes" : "No"
      };
      subjKeys.forEach(s => {
        const st = r.subjectStats?.[s] || {};
        base[`${s} Obtained`] = Number.isFinite(st.obtained) ? Math.round(st.obtained * 100) / 100 : "";
        base[`${s} Total`]    = st.total ?? "";
        base[`${s} %`]        = Number.isFinite(st.percent) ? Math.round(st.percent) : "";
        base[`${s} Rank`]     = st.rank ?? "";
      });
      return base;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Results");
    XLSX.writeFile(wb, `exam_${examId || "results"}.xlsx`);
  };

  const exportCSV = () => {
    const subjKeys = Object.keys(subjectTotals);
    const rows = filtered.map(r => {
      const base = {
        name: r.studentName || r.name || r.byStudent?.name || "",
        roll: r.roll || r.byStudent?.rollNo || "",
        class: r.className || "",
        section: r.section || "",
        branch: r.branch || "",
        obtained: Number.isFinite(r.obtained) ? r.obtained : "",
        total: r.total ?? "",
        percent: Number.isFinite(r.percent) ? Math.round(r.percent) : "",
        accuracy: Number.isFinite(r.accuracy) ? Math.round(r.accuracy) : "",
        timeTakenSec: r.timeTakenSec ?? "",
        rank: r.rank ?? "",
        percentile: Number.isFinite(r.percentile) ? Math.round(r.percentile) : "",
        status: r.status || "",
        submittedAt: fmt(toDate(r.submittedAt)),
        hasPDF: r.upload && (r.upload.path || r.upload.url) ? "Yes" : "No"
      };
      subjKeys.forEach(s => {
        const st = r.subjectStats?.[s] || {};
        base[`${s}_obtained`] = Number.isFinite(st.obtained) ? Math.round(st.obtained * 100) / 100 : "";
        base[`${s}_total`]    = st.total ?? "";
        base[`${s}_percent`]  = Number.isFinite(st.percent) ? Math.round(st.percent) : "";
        base[`${s}_rank`]     = st.rank ?? "";
      });
      return base;
    });

    const header = Object.keys(rows[0] || { name: "", roll: "" });
    const lines = [header.join(","), ...rows.map(r => header.map(h => JSON.stringify(r[h] ?? "")).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `exam_${examId || "results"}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };


  const RowData = useMemo(
    () => ({ rows: filtered, openPdf: openAttemptPdf, onViewExam: openDetail }),
    [filtered, openAttemptPdf, openDetail]
  );

  return (
    <Container fluid className="py-3">
      <ToastContainer containerId={TOAST_ID} position="bottom-right" newestOnTop closeOnClick={false} theme="dark" limit={4} />

      {/* Filters */}
            {/* Filters */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <Form.Select
          size="sm"
          style={{ width: 160 }}
          value={testType}
          onChange={(e) => setTestType(e.target.value)}
        >
          <option value="">All Test Types</option>
          {testTypeOptions.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Form.Select>

      <Form.Select
  size="sm"
  style={{ width: 220 }}
  value={examId}
  onChange={(e) => setExamId(e.target.value)}
  disabled={loadingExams}
>
  <option value="">All Exams</option> {/* ✅ still here */}
  {filteredExams.map((ex) => (
    <option key={ex.id} value={ex.id}>
      {ex.name || ex.id}
    </option>
  ))}
</Form.Select>


        <Form.Select
          size="sm"
          style={{ width: 120 }}
          value={cls}
          onChange={(e) => setCls(e.target.value)}
        >
          <option value="">All Classes</option>
          {classOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Form.Select>

        <Form.Select
          size="sm"
          style={{ width: 120 }}
          value={sec}
          onChange={(e) => setSec(e.target.value)}
        >
          <option value="">All Sections</option>
          {sectionOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Form.Select>

        <Form.Select
          size="sm"
          style={{ width: 140 }}
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
        >
          <option value="">All Branches</option>
          {branchOptions.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </Form.Select>

        <InputGroup size="sm" style={{ width: 200 }}>
          <InputGroup.Text>Search</InputGroup.Text>
          <Form.Control
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="By name or roll no"
          />
        </InputGroup>

        <Form.Check
          type="checkbox"
          label="Has PDF"
          checked={hasPdf}
          onChange={(e) => setHasPdf(e.target.checked)}
        />

        <Button
          size="sm"
          variant="outline-success"
          onClick={exportXLSX}
        >
          Export XLSX
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={exportCSV}
        >
          Export CSV
        </Button>
      </div>

      {/* KPIs */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="fw-semibold">Total Attempts</div>
              <div className="fs-4">{kpi.count}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="fw-semibold">Submitted</div>
              <div className="fs-4">{kpi.submitted}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="fw-semibold">Avg %</div>
              <div className="fs-4">{kpi.avgPct ?? "—"}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="fw-semibold">Topper</div>
              <div className="fs-6">
                {kpi.topper ? `${kpi.topper.name} (${kpi.topper.pct}%)` : "—"}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Subject Leaderboards (top 3 per subject) */}
      {Object.keys(subjectTotals).length > 0 && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Header className="fw-semibold">Subject Leaderboards</Card.Header>
          <Card.Body>
            <Row className="g-3">
              {Object.keys(subjectTotals).map(subject => {
                const top = [...attemptsEnriched]
                  .filter(r => Number.isFinite(r.subjectStats?.[subject]?.percent))
                  .sort((a,b) => (b.subjectStats[subject].percent - a.subjectStats[subject].percent))
                  .slice(0, 3);
                return (
                  <Col md={4} key={subject}>
                    <div className="fw-semibold mb-2">{subject}</div>
                    {top.length === 0 ? (
                      <div className="text-muted small">No data</div>
                    ) : (
                      <ol className="mb-0 small">
                        {top.map(r => (
                          <li key={r.id}>
                            {(r.studentName || r.name || r.byStudent?.name || "—")} — {Math.round(r.subjectStats[subject].percent)}%
                          </li>
                        ))}
                      </ol>
                    )}
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Histogram */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hist}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#0d6efd" />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {/* Results List */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="fw-semibold">Student Results</Card.Header>
        <Card.Body className="p-0">
          {loadingAttempts ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-4 text-muted">No results</div>
          ) : (
            <div style={{ height: listHeight, overflow: "auto" }}>
              <List
                height={listHeight}
                width="100%"
                itemCount={filtered.length}
                itemSize={isMobile ? ITEM_SIZE_MOBILE : ITEM_SIZE_DESKTOP}
                itemData={RowData}
                overscanCount={8}
              >
                {isMobile ? MobileRowItem : RowItem}
              </List>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* PDF Modal */}
      <PdfModal />

      {/* Detail Modal */}
      <Modal
        show={detailOpen}
        onHide={closeDetail}
        size="xl"
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Exam Analysis</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailRow && (
            <EmbeddedQuestionAnalysis
              schoolId={schoolId}
              examId={examId}
              uid={uidForRow(detailRow)}
            />
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}

